import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { MeetingDataService } from '@omni/data-services/meeting/meeting.data.service';
import { SummaryConversationCacheService } from '../../services/summary-conversation-cache.service';
import { AgentCategory } from '../../models/agent-config.model';
import { AiConversationItem } from '../../transports/ai-transport';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { peelMeetingAnalysis, peelTranscript } from '../../features/summary-item-peel';

/**
 * Tools that expose a meeting's transcript + AI analysis to the Assistant by
 * meeting id, independent of any in-scope meeting. Both resolve through the
 * Slice-1 conversation cache ladder (local items → Foundry by conv id → none),
 * so they work offline once a meeting has been analysed and cross-system after
 * a cold-cache fetch. Read-only.
 */
@Injectable({ providedIn: 'root' })
export class MeetingTools {
  private readonly activityService = inject(ActivityService);
  private readonly meetingDataService = inject(MeetingDataService);
  private readonly cache = inject(SummaryConversationCacheService);

  @AITool('get_meeting_analysis', "Get the AI analysis (summary, key messages, sentiment) of a meeting by its id", [
    { name: 'meeting_id', type: 'string', description: 'The meeting/activity id', required: true },
  ])
  async getMeetingAnalysis(meeting_id: string): Promise<any> {
    try {
      const resolved = await this.#resolveItems(meeting_id);
      if (!resolved.found) {
        return { success: false, error: 'Meeting not in scope — cannot retrieve its data on this device.' };
      }
      const analysis = resolved.items ? peelMeetingAnalysis(resolved.items) : null;
      return analysis
        ? { success: true, analysis }
        : { success: true, message: 'This meeting has no analysis yet.' };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get meeting analysis' };
    }
  }

  @AITool('get_meeting_transcript', "Get the full transcript of a meeting by its id", [
    { name: 'meeting_id', type: 'string', description: 'The meeting/activity id', required: true },
  ])
  async getMeetingTranscript(meeting_id: string): Promise<any> {
    try {
      const resolved = await this.#resolveItems(meeting_id);
      if (!resolved.found) {
        return { success: false, error: 'Meeting not in scope — cannot retrieve its data on this device.' };
      }
      const transcript = resolved.items ? peelTranscript(resolved.items) : null;
      return transcript
        ? { success: true, transcript }
        : { success: true, message: 'This meeting has no transcript yet.' };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get meeting transcript' };
    }
  }

  /**
   * Resolve a meeting's cached conversation items by id. Returns `{found:false}`
   * when the meeting isn't in the rep's synced activities (so the tool can say
   * "not in scope" rather than the misleading "no analysis"); `{found:true,
   * items}` otherwise — `items` may still be null when the meeting exists but
   * was never analysed.
   */
  async #resolveItems(meetingId: string): Promise<{ found: boolean; items?: AiConversationItem[] | null }> {
    const results = this.activityService.activities;
    const activities = Array.isArray(results) ? results : [];
    const activity = activities.find(a => a instanceof AppointmentActivity && a.ID === meetingId) as AppointmentActivity | undefined;
    if (!activity) return { found: false };
    const convId = await this.meetingDataService.resolveMeetingConversationId(activity);
    const items = await this.cache.resolve(AgentCategory.MeetingSummary, convId, DB_KEY_PREFIXES.NOVA_ANALYSIS + meetingId);
    return { found: true, items };
  }
}
